/**
 * L.8 — "What changed" heuristic deltas between base and LoRA output.
 *
 * Pure function. No LLM round-trip — we surface the most legible
 * differences a non-technical attorney would actually notice when
 * looking at two completions side by side:
 *
 *   - Length change (word count + percent).
 *   - Formality markers (does one use formal connectors like
 *     "pursuant to", "thereto", "heretofore"?).
 *   - Citation density (FRCP / U.S.C. / L.R. / case-style refs).
 *   - Passive-voice density (rough heuristic — "was/were + verb-ed").
 *   - First-line shape (heading vs flowing prose).
 *
 * The output is an ordered list of human-readable bullets, capped at
 * five so the card stays scannable. Each bullet is "Specialized X,
 * which the base [didn't|did Y]" or similar concrete comparison.
 *
 * Heuristic, not exhaustive. The point isn't precision — it's a
 * vocabulary for the user to start looking at the diff with. The
 * "Ask NIPE for more detail" button in the Lab takes it from there.
 */

const FORMAL_MARKERS = [
  'pursuant to',
  'thereto',
  'heretofore',
  'hereinafter',
  'aforementioned',
  'wherefore',
  'whereupon',
  'inasmuch as',
  'in light of',
  'notwithstanding',
];

const CITATION_PATTERNS = [
  /\bF\.?\s?R\.?\s?C\.?\s?P\.?\s*\d+/gi, // FRCP 26
  /\bL\.?\s?R\.?\s*\d+/gi, // L.R. 26.1
  /\b\d+\s*U\.?\s?S\.?\s?C\.?\s*§?\s*\d+/g, // 28 U.S.C. § 1331
  /\b\d{2,3}\s*F\.?\s?\d?d?\s*\d+/g, // 410 F.3d 100
  /\b§\s*\d+/g, // bare section markers
];

const PASSIVE_PATTERN = /\b(was|were|is|are|been|being)\s+\w+(ed|en)\b/gi;

function countMatches(text: string, pattern: RegExp): number {
  pattern.lastIndex = 0;
  let n = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    n += 1;
    if (m.index === pattern.lastIndex) pattern.lastIndex += 1;
  }
  return n;
}

function wordCount(text: string): number {
  const trimmed = text.trim();
  if (trimmed === '') return 0;
  return trimmed.split(/\s+/).length;
}

function formalMarkerCount(text: string): number {
  const lower = text.toLowerCase();
  let n = 0;
  for (const m of FORMAL_MARKERS) {
    let idx = 0;
    while ((idx = lower.indexOf(m, idx)) !== -1) {
      n += 1;
      idx += m.length;
    }
  }
  return n;
}

function citationCount(text: string): number {
  let n = 0;
  for (const p of CITATION_PATTERNS) {
    n += countMatches(text, new RegExp(p.source, p.flags));
  }
  return n;
}

function passiveCount(text: string): number {
  return countMatches(text, new RegExp(PASSIVE_PATTERN.source, PASSIVE_PATTERN.flags));
}

function startsWithHeading(text: string): boolean {
  const firstLine = text.trim().split(/\n/, 1)[0] ?? '';
  if (firstLine.length === 0) return false;
  if (/^#+\s/.test(firstLine)) return true;
  if (/^[IVX]+\.\s/.test(firstLine)) return true; // I. II. III.
  if (/^[A-Z][A-Z\s]+$/.test(firstLine) && firstLine.length < 60) return true;
  if (/^[A-Z][A-Z\s]+:/.test(firstLine)) return true;
  return false;
}

export interface WhatChangedResult {
  bullets: string[];
  /** Raw numeric deltas, exposed for tests + future card variants. */
  metrics: {
    baseWords: number;
    loraWords: number;
    baseFormal: number;
    loraFormal: number;
    baseCitations: number;
    loraCitations: number;
    basePassive: number;
    loraPassive: number;
    baseHeading: boolean;
    loraHeading: boolean;
  };
}

export function computeWhatChanged(baseText: string, loraText: string): WhatChangedResult {
  const baseWords = wordCount(baseText);
  const loraWords = wordCount(loraText);
  const baseFormal = formalMarkerCount(baseText);
  const loraFormal = formalMarkerCount(loraText);
  const baseCitations = citationCount(baseText);
  const loraCitations = citationCount(loraText);
  const basePassive = passiveCount(baseText);
  const loraPassive = passiveCount(loraText);
  const baseHeading = startsWithHeading(baseText);
  const loraHeading = startsWithHeading(loraText);

  const bullets: string[] = [];

  // Length delta — show when ≥25% difference in either direction.
  if (baseWords > 0 || loraWords > 0) {
    const ratio = loraWords === 0 || baseWords === 0 ? null : loraWords / baseWords;
    if (ratio !== null && ratio <= 0.75) {
      const pct = Math.round((1 - ratio) * 100);
      bullets.push(`Tightened length by ${pct}% (${baseWords} → ${loraWords} words).`);
    } else if (ratio !== null && ratio >= 1.25) {
      const pct = Math.round((ratio - 1) * 100);
      bullets.push(`Expanded length by ${pct}% (${baseWords} → ${loraWords} words).`);
    } else if (Math.abs(loraWords - baseWords) <= 5) {
      // matched length — skip (boring)
    } else {
      bullets.push(`Length: ${baseWords} → ${loraWords} words.`);
    }
  }

  // Citation density.
  if (loraCitations > baseCitations) {
    const delta = loraCitations - baseCitations;
    bullets.push(`Added ${delta} formal citation${delta === 1 ? '' : 's'} (FRCP / § / case refs).`);
  } else if (baseCitations > loraCitations) {
    const delta = baseCitations - loraCitations;
    bullets.push(`Dropped ${delta} formal citation${delta === 1 ? '' : 's'}.`);
  }

  // Formality.
  if (loraFormal > baseFormal) {
    const delta = loraFormal - baseFormal;
    bullets.push(`Used ${delta} more formal connector${delta === 1 ? '' : 's'} ("pursuant to", "thereto", etc.).`);
  } else if (baseFormal > loraFormal) {
    const delta = baseFormal - loraFormal;
    bullets.push(`Dropped ${delta} formal connector${delta === 1 ? '' : 's'}.`);
  }

  // Heading shape.
  if (loraHeading && !baseHeading) {
    bullets.push('Switched to a headed-paragraph structure (base wrote flowing prose).');
  } else if (baseHeading && !loraHeading) {
    bullets.push('Wrote flowing prose (base used a headed-paragraph structure).');
  }

  // Passive voice.
  if (Math.abs(loraPassive - basePassive) >= 2) {
    if (loraPassive < basePassive) {
      bullets.push(`Reduced passive voice (${basePassive} → ${loraPassive} instances).`);
    } else {
      bullets.push(`Used more passive voice (${basePassive} → ${loraPassive} instances).`);
    }
  }

  if (bullets.length === 0) {
    bullets.push(
      'The two outputs are very close — try a different prompt or re-run for a clearer contrast.',
    );
  }

  return {
    bullets: bullets.slice(0, 5),
    metrics: {
      baseWords,
      loraWords,
      baseFormal,
      loraFormal,
      baseCitations,
      loraCitations,
      basePassive,
      loraPassive,
      baseHeading,
      loraHeading,
    },
  };
}
